import { forEach, get, includes, split, last, memoize } from 'lodash';
import { AppFactory, Logger, sessionManager } from 'vue-infra';

import { NAMESPACE } from '@/common/consts.js';
import { getLoggerClientTime } from '@/common/utils.js';
import pkg from '../../package.json';

export const DIAGNOSTIC_KEYS = {
  initializationData: 'initializationData',
  ringsSubscriptionFlow: 'ringsSubscriptionFlow',
  ringsAcceptFlow: 'ringsAcceptFlow',
  takeOverConversationFlow: 'takeOverConversationFlow',
  conversationParticipantsJourney: 'conversationParticipantsJourney',
  maskedRecentlyClosedConversationTranscriptFlow: 'maskedRecentlyClosedConversationTranscriptFlow',
  transferConversationFlow: 'transferConversationFlow',
  screenResolution: 'screenResolution',
};

const DEFAULT_LOGGER_CONTEXT = 'log-diagnostics';
const DIAGNOSTICS_SITE_SETTINGS_FEATURE_KEY = 'le.agent.messaging.newAgentWorkspace.diagnostics';

const store = AppFactory.store;
const getSessionValue = store.getters['session/getValue'];

const consolePrefix = '[DiagUtils]';

let defaultLogger = null;

let accountId;
let agentId;
let loginName;
let accountLevelConfig;
const agentLevelConfig = {};

/*
 Private Interface
 ******************************************************* */

/**
 * Log Debug Message to Console
 *
 * Use for troubleshooting issues related to diagUtils
 *
 * (?) Set `localStorage.setItem('NewAgentWorkspace_debug', 'true')` to allow writing objects to the console
 *
 * @param {String} message
 * @param {...*} rest
 *
 * @internal
 */
function logDebug(message, ...rest) {
  const debugState = window.localStorage.getItem(`${NAMESPACE.NEW_AGENT_WORKSPACE}_debug`);
  if (debugState === 'true') {
    window.console.debug(`${consolePrefix} ${message}`, ...rest);
    // setting the key ourselves means we don't want to use the default logger
    return;
  }

  // sanity check
  if (defaultLogger === null) {
    return;
  }

  // @TODO: fix logger issue with printing objects to the console
  // @see https://lpgithub.dev.lprnd.net/WebJedi/vue-infra/blob/master/src/logger/appenders/ConsoleAppender.js#L61
  const debugInfo = rest && rest.length ? ` ${JSON.stringify(rest)}` : '';
  defaultLogger.debug(
    `${message}${debugInfo}`,
    `${NAMESPACE.NEW_AGENT_WORKSPACE}: ${consolePrefix}`,
  );
}

function sendLog({
  msg, namespace, payload, logger,
}) {
  const finalNamespace = `${NAMESPACE.NEW_AGENT_WORKSPACE}: ${namespace}`;
  const data = {
    clientTime: getLoggerClientTime(),
    accountId,
    agentId,
    loginName,
    moduleVersion: `${pkg.version}`,
    data: {
      ...payload,
    },
  };
  logDebug('sendLog::', msg, finalNamespace, data);
  logger.error(msg, finalNamespace, data);
}

/*
 Public Interface
 ******************************************************* */

/**
 * Initialize Diagnostics Functionality
 *
 * (!) Should be run during application initialization (the session is established prior to the initialization process)
 */
export function initializeDiagnostics() {
  // sanity check
  if (defaultLogger !== null) {
    return;
  }

  defaultLogger = Logger.getLogger(DEFAULT_LOGGER_CONTEXT);

  accountId = getSessionValue('accountId');
  agentId = getSessionValue('userId');
  loginName = getSessionValue('loginName');

  const diagConfig = sessionManager.getAccountSettingValueByID(DIAGNOSTICS_SITE_SETTINGS_FEATURE_KEY, {});
  logDebug('diagConfig::', diagConfig);

  // account level logs
  accountLevelConfig = get(diagConfig, 'accountLevelLogs', []);
  logDebug('accountLevelConfig::', accountLevelConfig);

  // agent level logs
  const agentLevelLogs = get(diagConfig, 'agentLevelLogs', []);
  // gracefully handle duplicate agent config objects
  forEach(agentLevelLogs, (agentConfig) => {
    // allow handling of accountId.agentId
    const agentConfigAgentId = last(split(get(agentConfig, 'agentId', ''), '.'));

    if (!agentLevelConfig[agentConfigAgentId]) {
      agentLevelConfig[agentConfigAgentId] = {};
    }
    const agentConfigKeys = get(agentConfig, 'keys', []);
    forEach(agentConfigKeys, (key) => {
      agentLevelConfig[agentConfigAgentId][key] = true;
    });
  });
  logDebug('agentLevelConfig::', agentLevelConfig);
}

/**
 * Is Diagnostic Key Active?
 *
 * (!) For backward compatibility (prior to 1.28), you'll need to add the diag keys to both `le.agent.diagnostics` and `le.agent.messaging.newAgentWorkspace.diagnostics`
 * (!) Starting with 1.28, you'll only need to add the diag keys to `le.agent.messaging.newAgentWorkspace.diagnostics`
 *
 * @param {String} key
 *
 * @return {Boolean}
 */
function isDiagnosticKeyActiveFn(key) {
  // account level support
  const activeByAccountLevelConfig = includes(accountLevelConfig, key);

  // agent level support
  const activeByAgentLevelConfig = !!(get(agentLevelConfig, [agentId, key], false));

  return (activeByAccountLevelConfig || activeByAgentLevelConfig);
}

/**
 * Memoized Is Diagnostic Key Active? Function
 *
 * Memoization is done based on the `key` param
 *
 * @name isDiagnosticKeyActive
 * @type {function(string)}
 * @param {String} key
 *
 * @return {Boolean}
 */
export const isDiagnosticKeyActive = memoize(isDiagnosticKeyActiveFn);

/**
 * Send Diagnostics Log
 *
 * @param {String} msg
 * @param {String} namespace
 * @param {Object} payload
 * @param {Object} [logger]
 */
export function sendDiagnosticLog(msg, namespace, payload, logger) {
  sendLog({
    msg, namespace, payload, logger: logger || defaultLogger,
  });
}
